import { useState, FormEvent } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  message: string;
  website: string;
}

interface FormErrors {
  name?: string;
  email?: string;
}

const inputClass = (hasError: boolean) =>
  `w-full rounded-[10px] border-[1.5px] bg-ak-navy/80 px-3.5 py-[11px] font-sans text-sm text-white outline-none transition-[border-color,box-shadow] duration-200 focus:border-ak-sky focus:shadow-[0_0_0_3px_rgba(253,224,71,0.15)] ${
    hasError ? 'border-red-500' : 'border-ak-sky/25'
  }`;

const ContactDialog = ({ isOpen, onClose }: Props) => {
  const [form, setForm] = useState<FormData>({ name: '', email: '', message: '', website: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!isOpen) return null;

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.name.trim()) e.name = 'Bitte geben Sie Ihren Namen ein.';
    else if (form.name.length > 100) e.name = 'Name darf maximal 100 Zeichen lang sein.';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Bitte geben Sie eine gültige Email-Adresse ein.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setPending(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          website: form.website,
        }),
      });
      if (res.status === 429) {
        setSubmitError('Zu viele Anfragen. Bitte versuchen Sie es in 15 Minuten erneut.');
        return;
      }
      const data = await res.json();
      if (res.ok && data.success) {
        setSent(true);
        setForm({ name: '', email: '', message: '', website: '' });
      } else {
        setSubmitError('Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } catch {
      setSubmitError('Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setPending(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSent(false);
    setSubmitError(null);
    setErrors({});
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-[460px] rounded-3xl border border-ak-sky/20 bg-ak-navy p-7 text-white shadow-[0_32px_64px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="mb-0.5 text-lg font-bold text-white">Kontakt aufnehmen</h2>
            <p className="m-0 text-xs text-ak-dim">Ich melde mich so schnell wie möglich</p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Dialog schließen"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0 bg-white/[0.06] text-base text-ak-muted transition-colors hover:bg-white/10"
          >
            <i className="pi pi-times"></i>
          </button>
        </div>

        {sent ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/15">
              <i className="pi pi-check text-[28px] text-green-500"></i>
            </div>
            <p className="mb-1.5 text-base font-semibold text-white">Nachricht gesendet!</p>
            <p className="m-0 text-sm text-ak-muted">Vielen Dank! Ich melde mich schnellstmöglich bei Ihnen.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3.5 flex flex-col gap-3.5">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ak-muted">
                  Ihr Name *
                </label>
                <input
                  className={inputClass(Boolean(errors.name))}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Max Mustermann"
                />
                {errors.name && <p className="mt-1 text-[11px] text-red-500">{errors.name}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ak-muted">
                  Ihre Email-Adresse *
                </label>
                <input
                  type="email"
                  className={inputClass(Boolean(errors.email))}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="max@beispiel.de"
                />
                {errors.email && <p className="mt-1 text-[11px] text-red-500">{errors.email}</p>}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-ak-muted">
                  Nachricht (optional)
                </label>
                <textarea
                  className={`${inputClass(false)} h-[90px] resize-none`}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Womit kann ich Ihnen helfen?"
                  maxLength={2000}
                />
              </div>
            </div>

            {/* Honeypot */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                value={form.website}
                onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {submitError && (
              <p className="mb-3 text-xs text-red-500">
                <i className="pi pi-exclamation-triangle mr-1.5"></i>
                {submitError}
              </p>
            )}

            <p className="mb-4 text-[11px] text-ak-dim">
              <i className="pi pi-lock mr-[5px] text-[10px]"></i>
              Ihre Daten werden ausschließlich zur Kontaktaufnahme genutzt.
            </p>

            {pending ? (
              <div className="flex items-center gap-2 text-[13px] text-gray-400">
                <i className="pi pi-spin pi-spinner"></i> Bitte warten…
              </div>
            ) : (
              <button type="submit" className="btn-primary text-sm">
                <i className="pi pi-send"></i> Nachricht senden
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactDialog;
